import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';

import {trackEvent} from 'analytics/Analytics';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import type {PredictionBatchSource} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import FileStates from 'FileStates';
import {getApolloClient} from 'graphql-client/ApolloClient';
import * as ApolloWrappers from 'graphql-client/ApolloWrappers';
import {
  aaSequenceId,
  buildPredictionBatchName,
  moleculeId,
  proteinId,
  submitBulkPrediction,
} from 'model-hub/scientific-models/input-forms/BulkPredictionUtils';
import {
  getComponentValidationResult,
  getValidationErrorMessages,
} from 'model-hub/scientific-models/input-forms/structure-prediction/ComponentUtils';
import {
  getUseMSAForSubmission,
  type ScientificModelNode,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import {AaSequenceStructureExternalFile, ProteinVersion} from 'molbio/aa-sequences/util/AaSequenceConstants';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';

import {RemainingExecutionsAndTenantNameQuery} from './__generated/RemainingExecutionsAndTenantNameQuery';
import AlphaFoldTabState from './AlphaFoldTabState';
import type {ViewState} from './AlphaFoldTabView';

export interface RequestRunParams {
  aaSequenceIds: Array<string>;
  moleculeIds: Array<string>;
  proteinIds: Array<string>;
  primaryAaSequenceId: string | undefined;
  folderId: string;
  remainingExecutionsAndTenantNameData: RemainingExecutionsAndTenantNameQuery;
  selectedModel: ScientificModelNode;
  msaEnabled: boolean;
  source?: PredictionBatchSource;
}

export async function requestRun({
  aaSequenceIds,
  moleculeIds,
  proteinIds,
  primaryAaSequenceId,
  folderId,
  remainingExecutionsAndTenantNameData,
  selectedModel,
  msaEnabled,
  source,
}: RequestRunParams): Promise<void> {
  if (!selectedModel.capabilities.supportsMultimers && proteinIds.length > 0) {
    throw new Error('proteinId components are only supported for multimer-capable models');
  }

  const predictionConfigs = selectedModel.capabilities.supportsMultimers
    ? [
        {
          components: [
            ...aaSequenceIds.map((id) => aaSequenceId(id)),
            ...proteinIds.map((id) => proteinId(id)),
            ...moleculeIds.map((id) => moleculeId(id)),
          ],
        },
      ]
    : [
        ...aaSequenceIds.map((id) => ({component: aaSequenceId(id)})),
        ...moleculeIds.map((id) => ({component: moleculeId(id)})),
      ];
  await submitBulkPrediction({
    folderId,
    model: selectedModel,
    predictionConfigs,
    modelParams: {useMsa: getUseMSAForSubmission(selectedModel, msaEnabled)},
    source,
    name: buildPredictionBatchName(selectedModel),
  });

  // Update Apollo cache to reflect the new prediction in progress
  ApolloWrappers.writeQuery(
    {query: REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY},
    {
      ...remainingExecutionsAndTenantNameData,
      remainingAlphafoldExecutions: remainingExecutionsAndTenantNameData.remainingAlphafoldExecutions - 1,
    }
  );

  // Since the list of in progress predictions is not just a boolean,
  // we need to evict the list because we do not have all of the information here to update the cache locally
  if (primaryAaSequenceId != null) {
    getApolloClient().cache.evict({
      // ProteinNode is an outdated GraphQL typename; it refers to an AA sequence node.
      id: `ProteinNode:${primaryAaSequenceId}`,
      fieldName: 'inProgressStructurePredictionJobs',
    });
  }
}

interface UnavailableStructureInfo {
  structureFile: null;
  model: null;
  modelVersion: null;
  structureFileOutOfDate: true;
  versionId: null;
  coordinateStructureId: null;
  createdAt: null;
}

const UNAVAILABLE_STRUCTURE_INFO: UnavailableStructureInfo = {
  structureFile: null,
  model: null,
  structureFileOutOfDate: true,
  modelVersion: null,
  versionId: null,
  coordinateStructureId: null,
  createdAt: null,
};

export interface AvailableStructureInfo {
  structureFile: AaSequenceStructureExternalFile;
  model: string;
  modelVersion: string;
  structureFileOutOfDate: boolean;
  versionId: number;
  createdAt: string;
  coordinateStructureId: string;
  componentIds: Array<string>;
}

/**
 * Utility to evaluate info about a Protein's AlphaFold structure files.
 *
 * structureFile: the most recent completed structure file available, or undefined if there are none.
 * structureFileOutOfDate: indicates whether structureFile belongs to the most recent protein version.
 */
export function getStructureInfoFromVersions(
  currentlyViewedAaSequenceVersionId: number,
  versions?: Array<ProteinVersion>
): UnavailableStructureInfo | AvailableStructureInfo {
  if (versions === undefined || versions.length === 0) {
    return UNAVAILABLE_STRUCTURE_INFO;
  }
  const currentVersion = versions.find((version) => version.id === currentlyViewedAaSequenceVersionId);

  // Return null values if the requested version doesn't exist
  if (!currentVersion) {
    return UNAVAILABLE_STRUCTURE_INFO;
  }

  const currentVersionStructures = currentVersion.structures;
  if (currentVersionStructures && currentVersionStructures.length) {
    const versionId = currentVersion.id;
    // Sort structures by creation date (ISO 8601 strings sort correctly lexicographically)
    // and select the most recent one (last after sorting)
    const sortedStructures = currentVersionStructures
      .slice()
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const structure = sortedStructures[sortedStructures.length - 1];
    return {
      structureFile: structure.externalFile,
      model: structure.model,
      modelVersion: structure.modelVersion,
      structureFileOutOfDate: false,
      versionId,
      coordinateStructureId: structure.id,
      createdAt: structure.createdAt,
      componentIds: structure.componentIds,
    };
  }

  const flattenedVersionStructures = versions.flatMap((version) =>
    version.structures.map((structure) => ({
      structure,
      versionId: version.id,
      model: structure.model,
      modelVersion: structure.modelVersion,
    }))
  );
  if (flattenedVersionStructures.length) {
    // Sort by creation date (ISO 8601 strings sort correctly lexicographically)
    // and select the most recent structure (last after sorting)
    const sortedStructures = flattenedVersionStructures
      .slice()
      .sort((a, b) => a.structure.createdAt.localeCompare(b.structure.createdAt));
    const {versionId, structure, model, modelVersion} = sortedStructures[sortedStructures.length - 1];
    return {
      structureFile: structure.externalFile,
      model,
      modelVersion,
      structureFileOutOfDate: true,
      versionId,
      coordinateStructureId: structure.id,
      createdAt: structure.createdAt,
      componentIds: structure.componentIds,
    };
  }
  return UNAVAILABLE_STRUCTURE_INFO;
}

export async function downloadStructureFile(
  structureFile: AaSequenceStructureExternalFile,
  proteinId: string,
  versionId: number
): Promise<void> {
  const signedURL = await performSignedFileQuery(structureFile.id, structureFile.url, proteinId, versionId);
  if (signedURL == null) {
    return;
  }
  window.open(signedURL, '_blank', 'noopener noreferrer');
  trackEvent(MOLBIO_ANALYTICS_ACTIONS.DOWNLOADED_ALPHAFOLD_STRUCTURE);
}

export function getCoordinateStructureId(coordinateStructure: AvailableStructureInfo): string {
  return coordinateStructure.structureFile.id;
}

export function getStructuresForVersion(
  versions?: Array<ProteinVersion>,
  currentVersionId?: number
): Array<AvailableStructureInfo> {
  if (!versions || versions.length === 0) {
    return [];
  }

  // If currentVersionId is provided, only return predictions from that version
  // Otherwise, return predictions from all versions (backward compatibility)
  const versionsToProcess = currentVersionId
    ? versions.filter((version) => version.id === currentVersionId)
    : versions;

  const allCoordinateStructures: Array<AvailableStructureInfo> = [];
  const fallbackCurrentVersionId = versions[versions.length - 1].id;

  // Iterate through filtered versions and collect their structures
  versionsToProcess.forEach((version) => {
    version.structures.forEach((structure) => {
      allCoordinateStructures.push({
        structureFile: structure.externalFile,
        model: structure.model,
        modelVersion: structure.modelVersion,
        versionId: version.id,
        coordinateStructureId: structure.id,
        createdAt: structure.createdAt,
        structureFileOutOfDate: version.id !== (currentVersionId || fallbackCurrentVersionId),
        componentIds: structure.componentIds,
      });
    });
  });

  // Sort by creation date in reverse chronological order (newest first)
  return allCoordinateStructures.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

export function findCoordinateStructureById(
  coordinateStructures: Array<AvailableStructureInfo>,
  coordinateStructureId: string
): AvailableStructureInfo | null {
  return coordinateStructures.find((p) => getCoordinateStructureId(p) === coordinateStructureId) || null;
}

export const REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY: DocumentNode = gql`
  query RemainingExecutionsAndTenantNameQuery @skipAudit @engComponent(name: MOLBIO) {
    remainingAlphafoldExecutions
    modelHubExecutionLimit
    currentTenant {
      ... on TenantNode {
        id
        displayName
      }
    }
  }
`;

export const USER_VERIFICATION_STATUS: DocumentNode = gql`
  query UserVerificationStatusQuery @skipAudit @engComponent(name: MOLBIO) {
    currentUser {
      ... on UserNode {
        id
        academic
        institution {
          id
          verified
        }
        verified
      }
    }
  }
`;

export const ACADEMIC_VERIFICATION_MESSAGE =
  'Structure prediction is only available to verified users. Please verify your email to continue.';

export function isAcademicUserBlocked(
  currentUser: {
    academic?: boolean;
    verified?: boolean;
    institution?: {verified?: boolean} | null;
  } | null
): boolean {
  if (!BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC) {
    return false;
  }
  if (currentUser?.verified) {
    return false;
  }
  if (BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT && currentUser?.verified) {
    return false;
  }
  return true;
}

export function getRunBlockingMessage(
  aminoAcids: string,
  permissions: Permissions,
  remainingRuns: number,
  model: ScientificModelNode
): string | null {
  const validationResult = getComponentValidationResult(
    {
      proteinComponents: {
        aminoAcids,
        count: 1,
        numHeavyAtoms: 0,
        entityType: ModelTypeConstants.PROTEIN,
        permissions,
      },
    },
    model
  );
  if (remainingRuns <= 0) {
    return 'You do not have any remaining structure prediction runs.';
  } else if (validationResult != null) {
    return getValidationErrorMessages(validationResult, true).join('\n');
  }
  return null;
}

/**
 * Opens a protein in the workspace and activates the 3D Structure tab.
 * Used when navigating to a protein from Model Hub prediction jobs or
 * after submitting a structure prediction from the form.
 */
export async function navigateToProteinStructureTab(proteinId: string): Promise<void> {
  SidepanelActions.close({autoOpenFile: false});

  const fileState = await FileStates.get().openFolderItem(proteinId, {open: true});
  if (!fileState) {
    return;
  }
  const existingTab = fileState.findTab(AlphaFoldTabState);
  if (existingTab != null) {
    existingTab.set({active: true, initialView: 'COORDINATE_STRUCTURES_LIST' as ViewState});
  }
}
